import React, { useCallback, useContext, useState } from 'react';

import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/AddCircle';
import { makeStyles } from '@material-ui/styles';

import { GlobalContext } from '../../../../../store';
import { IAward, TStandingType } from '../../../../../store/admin/eventAwards/store';
import { IMessageNotification } from '../../../../../store/notification/store';

import Button from '../../../../../components/Button';
import { SortingOptions } from '../../../../../contrib/utils';
import { sortBy } from '../../../../../contrib/sortBy';
import { blankAward } from '../Utils/blankAward';
import { AwardRow } from './Components/AwardRow';
import { SortIcon } from './Components/SortIcon';
import { usePlayersOptions } from './Hooks/usePlayersOptions.hook';

export interface IAssignAwardsTable {
  setNewAward: any;
  awards: IAward[];
  newAward: IAward;
  eventId: number;
  deleteAwards: any;
  updateAwards: any;
  createAwards: any;
  forceShowNewAwardRow: boolean;
}

interface ITouchedFields {
  [fieldName: string]: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%'
    },
    table: {
      minWidth: 500,
      overflow: 'visible'
    },
    tableWrapper: {
      overflow: 'visible'
    }
  })
);

export type IOnAwardChange = (attr: string) => (event: any) => void;

export const AssignAwardsTable = (props: IAssignAwardsTable) => {
  const {
    awards,
    setNewAward,
    newAward,
    deleteAwards,
    createAwards,
    updateAwards,
    eventId,
    forceShowNewAwardRow
  } = props;

  const {
    actions: {
      notifications: { push: pushNotification }
    },
    adminEventEnrollments
  } = useContext(GlobalContext);

  const classes = useStyles();

  const [showNewAwardRow, setShowNewAwardRow] = useState(forceShowNewAwardRow || false);

  const [playersOptions] = usePlayersOptions(adminEventEnrollments, eventId);

  const onRemoveAward = useCallback(
    (awardId: number) => async () => {
      await deleteAwards(awardId);

      await pushNotification(({
        status: 'unread',
        text: 'Award removed!',
        type: 'success'
      } as unknown) as IMessageNotification);
    },
    [newAward]
  );

  const onAwardChange: IOnAwardChange = (attr: string) => (event: any) => {
    const value = event
      ? attr === 'user' || attr === 'type'
        ? event.value
        : event.target
        ? event.target.value
        : event
      : null;

    setNewAward({
      ...newAward,
      [attr]: value,
      touched: { ...newAward.touched, [attr]: true }
    });
  };

  const toggleSetShowNewAwardRow = () => {
    setNewAward(blankAward);
    setShowNewAwardRow(!showNewAwardRow);
  };

  const onSaveAward = useCallback(async () => {
    const { id: awardId, ...award } = newAward;

    const touchedFields: ITouchedFields = award.touched ? award.touched : {};
    const awardToUpdate: { [attribute: string]: string } = {};

    Object.keys(touchedFields).map((attribute: string) => {
      if (touchedFields[attribute]) {
        awardToUpdate[attribute] = award[attribute as keyof typeof award];
      }
    });

    const saveAction =
      awardId < 0 ? await createAwards(award) : await updateAwards(awardId, awardToUpdate);

    if (saveAction.errors) {
      await pushNotification(({
        status: 'unread',
        text: saveAction.errors.adminEventAwards.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      await pushNotification(({
        status: 'unread',
        text: `Award ${awardId < 0 ? 'created' : 'updated'}!`,
        type: 'success'
      } as unknown) as IMessageNotification);

      setNewAward(blankAward);

      if (awardId < 0) {
        toggleSetShowNewAwardRow();
      }
    }

    return saveAction;
  }, [newAward]);

  type ISortableProperties = TStandingType | '';

  const [orderBy, setOrderBy] = useState<ISortableProperties>('');
  const [order, setOrder] = useState<SortingOptions>('asc');

  const onSetOrderBy = (attribute: ISortableProperties) => () => {
    setOrder(order === 'asc' ? 'dec' : 'asc');
    setOrderBy(attribute);
  };

  return (
    <div className="assign-awards">
      <div className="assign-awards-table">
        {awards.length > 0 || forceShowNewAwardRow ? (
          <div className="assign-awards-table">
            <div className={classes.tableWrapper}>
              <Table className={classes.table}>
                <colgroup>
                  {[0, 1, 2, 3, 4, 6].map(index => (
                    <col key={index} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className="name">Player</TableCell>
                    <TableCell className="value">Award type</TableCell>
                    <TableCell className="value">
                      <div className="container">
                        <SortIcon
                          attribute="points"
                          orderBy={orderBy}
                          order={order}
                          onClick={onSetOrderBy('points')}
                        />
                        <span>Points</span>
                      </div>
                    </TableCell>
                    <TableCell className="value">
                      <div className="container">
                        <SortIcon
                          attribute="scrip"
                          orderBy={orderBy}
                          order={order}
                          onClick={onSetOrderBy('scrip')}
                        />
                        <span>Scrip</span>
                      </div>
                    </TableCell>
                    <TableCell className="value">
                      <div className="container">
                        <SortIcon
                          attribute="cash"
                          orderBy={orderBy}
                          order={order}
                          onClick={onSetOrderBy('cash')}
                        />
                        <span>Cash</span>
                      </div>
                    </TableCell>
                    <TableCell className="value">Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortBy(awards, order, orderBy).map((award: IAward) => (
                    <AwardRow
                      key={`award-${award.id}`}
                      award={award}
                      newAward={newAward}
                      onAwardChange={onAwardChange}
                      playersOptions={playersOptions}
                      onSave={onSaveAward}
                      onRemove={onRemoveAward}
                      toggleSetShowNewAwardRow={toggleSetShowNewAwardRow}
                    />
                  ))}

                  {showNewAwardRow && (
                    <AwardRow
                      award={newAward}
                      newAward={newAward}
                      onAwardChange={onAwardChange}
                      playersOptions={playersOptions}
                      onSave={onSaveAward}
                      toggleSetShowNewAwardRow={toggleSetShowNewAwardRow}
                      newAwardRow={true}
                    />
                  )}
                </TableBody>
                <TableFooter>
                  {!showNewAwardRow && (
                    <TableRow>
                      <TableCell className="add-new-award-button-row" colSpan={6}>
                        <Button color="primary" type="submit" onClick={toggleSetShowNewAwardRow}>
                          <AddIcon />
                          Add another award
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              </Table>
            </div>
          </div>
        ) : (
          <p className="noAwardsText">
            Assign points, scrip, or cash awards to players for their achievements in this round or
            event.
          </p>
        )}
      </div>
    </div>
  );
};

export default AssignAwardsTable;
